import type { WidgetType } from "../../components/Dashboard/widgets/registry";
import { getHeaders, PURCHASER_API_BASE_URL } from "../Constants";

// Column span for widgets in the dashboard grid
export type WidgetColumnSpan = 1 | 2 | 3;

// Row span (height) for widgets in the dashboard grid. Same shape as column span;
// 1 = compact, 2 = default, 3 = full-screen on a laptop.
export type WidgetRowSpan = 1 | 2 | 3;

// Dashboard types
export type DashboardType = "home" | "custom";

// API response type for a widget
export interface DashboardWidgetAPI {
    id: number;
    dashboardId: number;
    widgetType: WidgetType;
    size: WidgetColumnSpan;
    rowSpan: WidgetRowSpan;
    position: number;
    startsNewRow: boolean;
    config?: Record<string, unknown> | null;
    createdAt: string;
    updatedAt: string;
}

// API response type for a dashboard
export interface Dashboard {
    id: number;
    teamId: number;
    createdByUserId: number | null;
    createdByUser?: {
        id: number;
        first_name: string;
        last_name: string;
    };
    name: string | null;
    type: DashboardType;
    isDefault: boolean;
    isHomePage?: boolean; // Computed per-user by API
    widgets: DashboardWidgetAPI[];
    createdAt: string;
    updatedAt: string;
}

// Payload for adding a widget
export interface AddWidgetPayload {
    widgetType: WidgetType;
    size: WidgetColumnSpan;
    rowSpan?: WidgetRowSpan;
    position: number;
    startsNewRow?: boolean;
    config?: Record<string, unknown>;
}

// Payload for updating a widget
export interface UpdateWidgetPayload {
    size?: WidgetColumnSpan;
    rowSpan?: WidgetRowSpan;
    position?: number;
    startsNewRow?: boolean;
    config?: Record<string, unknown>;
}

// Payload for bulk reordering widgets
export interface ReorderWidgetsPayload {
    widgets: Array<{
        id: number;
        position: number;
        startsNewRow?: boolean;
    }>;
}

// Payload for creating a new dashboard
export interface CreateDashboardPayload {
    name: string;
    type?: DashboardType;
    templateId?: string;
}

// Payload for updating dashboard metadata
export interface UpdateDashboardPayload {
    name?: string;
}

export default {
    /**
     * Get a specific dashboard by its numeric ID
     */
    getDashboardById: async (dashboardId: number): Promise<Dashboard> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/dashboards/${dashboardId}`, {
            headers: getHeaders(),
        });

        if (!res.ok) {
            throw new Error("Failed to fetch dashboard");
        }

        return res.json();
    },

    /**
     * Get the user's default dashboard for a given type.
     * If no dashboard exists, the backend will create one with default widgets.
     */
    getDashboard: async (type: DashboardType): Promise<Dashboard> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/dashboards/type/${type}`, {
            headers: getHeaders(),
        });

        if (!res.ok) {
            throw new Error(`Failed to fetch ${type} dashboard`);
        }

        return res.json();
    },

    /**
     * Add a widget to a dashboard
     */
    addWidget: async (dashboardId: number, payload: AddWidgetPayload): Promise<DashboardWidgetAPI> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/dashboards/${dashboardId}/widgets`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            throw new Error("Failed to add widget");
        }

        return res.json();
    },

    /**
     * Update a widget's properties (size, position, config, etc.)
     */
    updateWidget: async (dashboardId: number, widgetId: number, payload: UpdateWidgetPayload): Promise<DashboardWidgetAPI> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/dashboards/${dashboardId}/widgets/${widgetId}`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            throw new Error("Failed to update widget");
        }

        return res.json();
    },

    /**
     * Remove a widget from a dashboard
     */
    removeWidget: async (dashboardId: number, widgetId: number): Promise<void> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/dashboards/${dashboardId}/widgets/${widgetId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        });

        if (!res.ok) {
            throw new Error("Failed to remove widget");
        }
    },

    /**
     * Bulk reorder widgets (update positions and startsNewRow flags)
     */
    reorderWidgets: async (dashboardId: number, payload: ReorderWidgetsPayload): Promise<Dashboard> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/dashboards/${dashboardId}/widgets/reorder`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            throw new Error("Failed to reorder widgets");
        }

        return res.json();
    },

    /**
     * Get all dashboards for the current user
     */
    getAllDashboards: async (): Promise<Dashboard[]> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/dashboards`, {
            headers: getHeaders(),
        });

        if (!res.ok) {
            throw new Error("Failed to fetch dashboards");
        }

        return res.json();
    },

    /**
     * Create a new dashboard (optionally from a template)
     */
    createDashboard: async (payload: CreateDashboardPayload): Promise<Dashboard> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/dashboards`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            throw new Error("Failed to create dashboard");
        }

        return res.json();
    },

    /**
     * Update dashboard metadata (name, isHomePage)
     */
    updateDashboard: async (dashboardId: number, payload: UpdateDashboardPayload): Promise<Dashboard> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/dashboards/${dashboardId}`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            throw new Error("Failed to update dashboard");
        }

        return res.json();
    },

    /**
     * Delete a dashboard
     */
    deleteDashboard: async (dashboardId: number): Promise<void> => {
        const { "Content-Type": _, ...headers } = getHeaders();

        const res = await fetch(`${PURCHASER_API_BASE_URL}/dashboards/${dashboardId}`, {
            method: "DELETE",
            headers,
            body: JSON.stringify({}),
        });

        if (!res.ok) {
            throw new Error("Failed to delete dashboard");
        }
    },

    /**
     * Set a dashboard as the home page
     */
    setHomeDashboard: async (dashboardId: number): Promise<Dashboard> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/dashboards/${dashboardId}/set-home`, {
            method: "POST",
            headers: getHeaders(),
            // getHeaders sets Content-Type: application/json, so Fastify expects
            // a JSON body. The route doesn't read any fields, but it needs the
            // bytes — empty object satisfies the parser.
            body: JSON.stringify({}),
        });

        if (!res.ok) {
            throw new Error("Failed to set home dashboard");
        }

        // Server re-issues the JWT with the new homeDashboardId — pick it up so
        // subsequent requests (e.g., GET /dashboards refetch) authenticate with
        // the fresh value and compute isHomePage correctly.
        const newToken = res.headers.get("X-Auth-Token");
        if (newToken) localStorage.setItem("token", newToken);

        return res.json();
    },

    /**
     * Duplicate an existing dashboard
     */
    duplicateDashboard: async (dashboardId: number, name?: string): Promise<Dashboard> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/dashboards/${dashboardId}/duplicate`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ name }),
        });

        if (!res.ok) {
            throw new Error("Failed to duplicate dashboard");
        }

        return res.json();
    },
};
